import { useEffect, useRef, useState } from 'react';
import { SearchField } from '@backstage/ui';

import styles from './FilterSection.module.css';

export interface SearchFilterProps {
  /**
   * Committed search term. The input follows it when it changes from outside
   * (URL navigation, reset), dropping any pending debounced change, and
   * `onChange` is not called while the debounced term equals it.
   */
  value?: string;
  /** Called with the trimmed term once typing pauses for `debounceMs`. */
  onChange: (value: string) => void;
  /** @defaultValue 'Search' */
  placeholder?: string;
  /** Accessible label. Defaults to `placeholder`. */
  label?: string;
  /** @defaultValue 250 */
  debounceMs?: number;
  className?: string;
}

/**
 * Debounced free-text search for a filter bar.
 *
 * @example
 * <SearchFilter
 *   value={state.searchFilter}
 *   onChange={term => dispatch({ type: 'SET_SEARCH_FILTER', payload: term })}
 * />
 */
export const SearchFilter = ({
  value = '',
  onChange,
  placeholder = 'Search',
  label,
  debounceMs = 250,
  className,
}: SearchFilterProps) => {
  const [search, setSearch] = useState(value);
  const [syncedValue, setSyncedValue] = useState(value);
  const onChangeRef = useRef(onChange);

  // Adopt external changes to `value` before the debounce effect runs, so a
  // stale input can't be committed back over them. Our own commits already
  // match the trimmed input and leave it (and any trailing space) alone.
  if (value !== syncedValue) {
    setSyncedValue(value);
    if (value !== search.trim()) {
      setSearch(value);
    }
  }
  onChangeRef.current = onChange;

  useEffect(() => {
    const term = search.trim();
    if (term === value) {
      return undefined;
    }
    const timer = setTimeout(() => onChangeRef.current(term), debounceMs);
    return () => clearTimeout(timer);
  }, [search, value, debounceMs]);

  return (
    <SearchField
      name="search"
      aria-label={label ?? placeholder}
      placeholder={placeholder}
      value={search}
      onChange={setSearch}
      className={[styles.search, className].filter(Boolean).join(' ')}
    />
  );
};
